/**
 * Encryption Key Manager
 *
 * Secure management and validation of encryption keys for the application.
 * Ensures keys meet security requirements and provides helpful warnings
 * for development and production environments.
 *
 * @module encryptionKeyManager
 */

import { logger } from '../lib/logger';

/**
 * Security requirements for encryption keys
 */
const KEY_REQUIREMENTS = {
  MIN_LENGTH: 32,
  RECOMMENDED_LENGTH: 44, // Base64 encoded 32 bytes = 44 characters
  MAX_LENGTH: 256,
} as const;

/**
 * Known weak or example keys that should never be used
 */
const WEAK_KEYS = [
  'CHANGE_THIS_TO_A_RANDOM_32_CHAR_STRING_IN_PRODUCTION',
  'fallback_dev_key_do_not_use_in_prod',
  'dev_key',
  'test_key',
  'example_key',
  'default_key',
  'secret',
  'password',
  '12345678',
  'abcdefgh',
] as const;

/**
 * Key validation result
 */
export interface KeyValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
  strength: 'weak' | 'moderate' | 'strong';
}

/**
 * Validates an encryption key and returns detailed results
 *
 * @param key - The encryption key to validate
 * @param environment - Current environment (development, staging, production)
 * @returns Validation result with errors and warnings
 */
export function validateEncryptionKey(
  key: string | undefined,
  environment: string = 'development'
): KeyValidationResult {
  const result: KeyValidationResult = {
    isValid: true,
    warnings: [],
    errors: [],
    strength: 'strong',
  };

  // Check if key exists
  if (!key || key.trim() === '') {
    result.isValid = false;
    result.errors.push('Encryption key is missing or empty');
    result.strength = 'weak';
    return result;
  }

  const trimmedKey = key.trim();

  // Check minimum length
  if (trimmedKey.length < KEY_REQUIREMENTS.MIN_LENGTH) {
    result.isValid = false;
    result.errors.push(
      `Encryption key is too short (${trimmedKey.length} characters). ` +
        `Minimum required: ${KEY_REQUIREMENTS.MIN_LENGTH} characters`
    );
    result.strength = 'weak';
  }

  // Check maximum length (prevent potential memory issues)
  if (trimmedKey.length > KEY_REQUIREMENTS.MAX_LENGTH) {
    result.warnings.push(
      `Encryption key is unusually long (${trimmedKey.length} characters). ` +
        `Maximum recommended: ${KEY_REQUIREMENTS.MAX_LENGTH} characters`
    );
  }

  // Check for weak/example keys
  const lowerKey = trimmedKey.toLowerCase();
  for (const weakKey of WEAK_KEYS) {
    if (lowerKey.includes(weakKey.toLowerCase())) {
      result.isValid = environment === 'development'; // Allow in dev, block in prod
      const message = `Encryption key contains weak/example pattern: "${weakKey}"`;

      if (environment === 'production') {
        result.errors.push(`${message} - This is NOT allowed in production!`);
      } else {
        result.warnings.push(
          `${message} - Replace before deploying to production`
        );
      }
      result.strength = 'weak';
    }
  }

  // Check for common patterns
  const hasRepeatingChars = /(.)\1{4,}/.test(trimmedKey);
  if (hasRepeatingChars) {
    result.warnings.push(
      'Encryption key contains repeating character patterns'
    );
    result.strength = result.strength === 'strong' ? 'moderate' : 'weak';
  }

  // Check for sequential patterns
  const hasSequential = /(?:abc|bcd|cde|def|123|234|345|456|567|678|789)/i.test(
    trimmedKey
  );
  if (hasSequential) {
    result.warnings.push('Encryption key contains sequential patterns');
    result.strength = result.strength === 'strong' ? 'moderate' : 'weak';
  }

  // Check for environment-specific requirements
  if (environment === 'production') {
    // Production requires strong keys
    if (trimmedKey.length < KEY_REQUIREMENTS.RECOMMENDED_LENGTH) {
      result.warnings.push(
        `Production environment should use keys of at least ${KEY_REQUIREMENTS.RECOMMENDED_LENGTH} characters ` +
          `(current: ${trimmedKey.length})`
      );
      result.strength = 'moderate';
    }

    // Check for dev-specific patterns in production
    const devPatterns = ['dev', 'test', 'local', 'example', 'demo'];
    const hasDevPattern = devPatterns.some((pattern) =>
      lowerKey.includes(pattern)
    );
    if (hasDevPattern) {
      result.isValid = false;
      result.errors.push(
        'Encryption key appears to be a development key (contains dev/test/local/example/demo). ' +
          'Use a production-grade key!'
      );
      result.strength = 'weak';
    }
  }

  // Calculate entropy (basic check for randomness)
  const entropy = calculateEntropy(trimmedKey);
  if (entropy < 3.5) {
    result.warnings.push(
      'Encryption key has low entropy (not random enough). ' +
        'Use a cryptographically secure random generator'
    );
    result.strength = 'weak';
  } else if (entropy < 4.5 && result.strength === 'strong') {
    result.strength = 'moderate';
  }

  return result;
}

/**
 * Calculate Shannon entropy of a string (measure of randomness)
 *
 * @param str - String to analyze
 * @returns Entropy value (higher = more random)
 */
function calculateEntropy(str: string): number {
  const len = str.length;
  const frequencies: Record<string, number> = {};

  // Count character frequencies
  for (const char of str) {
    frequencies[char] = (frequencies[char] || 0) + 1;
  }

  // Calculate entropy
  let entropy = 0;
  for (const freq of Object.values(frequencies)) {
    const p = freq / len;
    entropy -= p * Math.log2(p);
  }

  return entropy;
}

/**
 * Get the encryption key from environment with validation
 *
 * @returns Validated encryption key
 * @throws Error if key is invalid in production
 */
export function getEncryptionKey(): string {
  const key = import.meta.env.VITE_ENCRYPTION_KEY;
  const environment: string = 'development';

  const validation = validateEncryptionKey(key, environment);

  // Log errors
  if (validation.errors.length > 0) {
    logger.error('Encryption key validation errors:', {
      errors: validation.errors,
    });
  }

  // Log warnings
  if (validation.warnings.length > 0) {
    logger.warn('Encryption key validation warnings:', {
      warnings: validation.warnings,
    });
  }

  // Log strength
  if (validation.strength !== 'strong') {
    logger.warn(`Encryption key strength: ${validation.strength}`);
  }

  // In production, throw error if key is invalid
  if (!validation.isValid && environment === 'production') {
    throw new Error(
      'Invalid encryption key in production environment. ' +
        'Please set a strong VITE_ENCRYPTION_KEY. ' +
        'Generate one using: openssl rand -base64 32'
    );
  }

  // In development, use fallback with warning
  if (!validation.isValid && environment === 'development') {
    logger.warn(
      '⚠️  Using fallback development encryption key. ' +
        'Set VITE_ENCRYPTION_KEY in .env file for production'
    );
    return 'dev_fallback_key_only_for_local_testing_32chars';
  }

  return key || 'dev_fallback_key_only_for_local_testing_32chars';
}

/**
 * Generate a secure encryption key using crypto API
 * This is a helper for development - in production, use openssl or similar
 *
 * @param length - Number of bytes (default: 32 = 256 bits)
 * @returns Base64-encoded random key
 */
export function generateSecureKey(length: number = 32): string {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    throw new Error('Web Crypto API not available');
  }

  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);

  // Convert to base64
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Initialize and validate encryption key on app startup
 * Call this in your app initialization
 */
export function initializeEncryptionKey(): void {
  const key = import.meta.env.VITE_ENCRYPTION_KEY;
  const environment: string = 'development';

  const validation = validateEncryptionKey(key, environment);

  // Display startup message
  console.log('🔐 Encryption Key Validation:');
  console.log(`   Environment: ${environment}`);
  console.log(`   Key Length: ${key?.length || 0} characters`);
  console.log(`   Strength: ${validation.strength}`);
  console.log(`   Valid: ${validation.isValid ? '✅' : '❌'}`);

  if (validation.errors.length > 0) {
    console.error('   Errors:');
    validation.errors.forEach((error) => console.error(`   - ${error}`));
  }

  if (validation.warnings.length > 0) {
    console.warn('   Warnings:');
    validation.warnings.forEach((warning) => console.warn(`   - ${warning}`));
  }

  if (
    environment === 'development' &&
    (!key || validation.strength === 'weak')
  ) {
    console.log('\n💡 To generate a secure key, run:');
    console.log('   openssl rand -base64 32');
    console.log('\n   Then add to .env:');
    console.log('   VITE_ENCRYPTION_KEY="<generated_key>"');
  }

  if (!validation.isValid && environment === 'production') {
    throw new Error(
      'Cannot start application with invalid encryption key in production'
    );
  }
}

/**
 * Check if running in a secure context (HTTPS or localhost)
 * Web Crypto API requires secure context for certain operations
 */
export function isSecureContext(): boolean {
  if (typeof window === 'undefined') {
    return true; // Server-side is considered secure
  }

  return (
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}
